import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, RefreshCw, X } from 'lucide-react';
import { validateKycSelfieFile } from './kycComplianceData';

/**
 * KYC selfie picker: upload a photo OR capture a live selfie via webcam.
 * Emits a File (jpg/png) through onChange, or null when cleared.
 */
export default function KycSelfieCapture({
  value = null,
  onChange,
  disabled = false,
  fileHint = 'JPG or PNG',
  onError,
}) {
  const [mode, setMode] = useState('upload');
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopCamera = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError('');
    stopCamera();
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not supported in this browser. Please upload a selfie instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraReady(true);
    } catch {
      setCameraError('Unable to access camera. Allow camera permission, or upload a selfie instead.');
      setCameraReady(false);
    }
  }, [stopCamera]);

  useEffect(() => {
    if (mode !== 'live' || value || disabled) {
      stopCamera();
      return undefined;
    }
    startCamera();
    return () => stopCamera();
  }, [mode, value, disabled, startCamera, stopCamera]);

  useEffect(() => {
    if (!value) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const clearSelfie = () => {
    onChange?.(null);
    onError?.('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadPick = (e) => {
    const input = e.target;
    const file = input.files?.[0] ?? null;
    if (!file) {
      onChange?.(null);
      return;
    }
    const err = validateKycSelfieFile(file);
    if (err) {
      onError?.(err);
      input.value = '';
      onChange?.(null);
      return;
    }
    onError?.('');
    onChange?.(file);
  };

  const captureLive = async () => {
    const video = videoRef.current;
    if (!video || !cameraReady) return;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      onError?.('Could not capture selfie. Please try again or upload a photo.');
      return;
    }

    ctx.translate(width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) {
      onError?.('Could not capture selfie. Please try again or upload a photo.');
      return;
    }

    const file = new File([blob], `kyc-selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
    const err = validateKycSelfieFile(file);
    if (err) {
      onError?.(err);
      return;
    }

    onError?.('');
    onChange?.(file);
    stopCamera();
  };

  const switchMode = (next) => {
    if (disabled || next === mode) return;
    clearSelfie();
    setCameraError('');
    setMode(next);
  };

  return (
    <div className="kyc-selfie">
      <div className="kyc-selfie-header">
        <label className="kyc-label">Selfie</label>
        <p className="kyc-hint">
          Required for identity verification. Take a clear live selfie or upload a recent photo of your face.
          Face must match your ID photo. {fileHint}
        </p>
      </div>

      <div className="kyc-selfie-mode-toggle" role="tablist" aria-label="Selfie method">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'upload'}
          className={`kyc-selfie-mode-btn${mode === 'upload' ? ' is-active' : ''}`}
          onClick={() => switchMode('upload')}
          disabled={disabled}
        >
          <ImagePlus size={16} strokeWidth={2} aria-hidden />
          Upload photo
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'live'}
          className={`kyc-selfie-mode-btn${mode === 'live' ? ' is-active' : ''}`}
          onClick={() => switchMode('live')}
          disabled={disabled}
        >
          <Camera size={16} strokeWidth={2} aria-hidden />
          Live selfie
        </button>
      </div>

      {value && previewUrl ? (
        <div className="kyc-selfie-preview-wrap">
          <img className="kyc-selfie-preview" src={previewUrl} alt="Selfie preview" />
          <div className="kyc-selfie-preview-actions">
            <span className="kyc-file-name">{value.name}</span>
            <button
              type="button"
              className="kyc-btn kyc-btn--ghost kyc-btn--sm"
              onClick={() => {
                clearSelfie();
                if (mode === 'live') startCamera();
              }}
              disabled={disabled}
            >
              <RefreshCw size={14} strokeWidth={2} aria-hidden />
              Retake
            </button>
            <button
              type="button"
              className="kyc-btn kyc-btn--ghost kyc-btn--sm kyc-selfie-clear"
              onClick={clearSelfie}
              disabled={disabled}
              aria-label="Remove selfie"
            >
              <X size={14} strokeWidth={2} aria-hidden />
              Remove
            </button>
          </div>
        </div>
      ) : mode === 'upload' ? (
        <div className="kyc-selfie-upload kyc-file-picker">
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            capture="user"
            onChange={handleUploadPick}
            className="kyc-file-input"
            disabled={disabled}
          />
          <button
            type="button"
            className="kyc-file-picker__btn"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
          >
            <ImagePlus size={15} strokeWidth={2.25} aria-hidden />
            Choose file
          </button>
          {!value ? <span className="kyc-file-picker__empty">No file chosen</span> : null}
        </div>
      ) : (
        <div className="kyc-selfie-live">
          {cameraError ? (
            <div className="kyc-selfie-camera-error" role="alert">
              <p>{cameraError}</p>
              <button type="button" className="kyc-btn kyc-btn--ghost kyc-btn--sm" onClick={startCamera} disabled={disabled}>
                Try camera again
              </button>
            </div>
          ) : (
            <>
              <div className="kyc-selfie-video-frame">
                <video
                  ref={videoRef}
                  className="kyc-selfie-video"
                  playsInline
                  muted
                  autoPlay
                />
                <div className="kyc-selfie-oval" aria-hidden />
              </div>
              <div className="kyc-selfie-live-actions">
                <button
                  type="button"
                  className="kyc-btn kyc-btn--primary kyc-btn--sm"
                  onClick={captureLive}
                  disabled={disabled || !cameraReady}
                >
                  <Camera size={16} strokeWidth={2} aria-hidden />
                  {cameraReady ? 'Capture selfie' : 'Starting camera…'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
